import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Navigation from '../components/layout/Navigation';

// ——— Configure your Valentine's song here ———
// Option A: Spotify track — paste your track ID from the song URL (e.g. open.spotify.com/track/XXXXX)
const VALENTINES_SPOTIFY_TRACK_ID = ''; // e.g. '1abc2def3ghi4jkl'
// Option B: Your own audio file — put an MP3 in public/ and set the path (e.g. '/valentines-2026.mp3')
const VALENTINES_AUDIO_SRC = '/never-let-u-go-final.wav';
const SONG_TITLE = 'never let u go';
const DEDICATION = 'made with love';
// ———

const Valentines2026: React.FC = () => {
  const hasSpotify = Boolean(VALENTINES_SPOTIFY_TRACK_ID?.trim());
  const hasAudio = Boolean(VALENTINES_AUDIO_SRC?.trim());
  const showPlayer = hasSpotify || hasAudio;

  return (
    <PageContainer>
      <Navigation />

      <Content
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
      >
        <Header>
          <Badge
            as={motion.span}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            Valentine's 2026
          </Badge>
          <Title
            as={motion.h1}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            A song for you
          </Title>
          <Subtitle
            as={motion.p}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            {DEDICATION}
          </Subtitle>
        </Header>

        <Card
          as={motion.div}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          {showPlayer ? (
            <>
              <SongInfo>
                <SongTitle>{SONG_TITLE}</SongTitle>
              </SongInfo>

              {hasSpotify ? (
                <SpotifyEmbed>
                  <iframe
                    src={`https://open.spotify.com/embed/track/${VALENTINES_SPOTIFY_TRACK_ID}?utm_source=generator&theme=0`}
                    width="100%"
                    height="152"
                    frameBorder="0"
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                    loading="lazy"
                    title="Valentine's Song"
                  />
                </SpotifyEmbed>
              ) : (
                <AudioWrap>
                  <audio controls src={VALENTINES_AUDIO_SRC} style={{ width: '100%' }}>
                    Your browser does not support the audio element.
                  </audio>
                </AudioWrap>
              )}
            </>
          ) : (
            <Placeholder>
              <PlaceholderTitle>Your song will go here</PlaceholderTitle>
              <PlaceholderText>
                Add your track: set <code>VALENTINES_SPOTIFY_TRACK_ID</code> in{' '}
                <code>src/pages/Valentines2026.tsx</code> for a Spotify link, or put an MP3 in{' '}
                <code>public/</code> and set <code>VALENTINES_AUDIO_SRC</code>.
              </PlaceholderText>
            </Placeholder>
          )}
        </Card>

        <Footer
          as={motion.p}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          Happy Valentine's Day 💜

          <br/><br/>

          only you could have me writing whole songs for you
          <br/>
          <br/> my inspiration, my motivation, my muse,
          <br/> my everything
        </Footer>
      </Content>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(160deg, #1a1a2e 0%, #16213e 40%, #0f3460 70%, #1a1a2e 100%);
  position: relative;
  overflow-x: hidden;
`;

const Content = styled(motion.div)`
  padding: 120px 2rem 4rem;
  max-width: 560px;
  margin: 0 auto;
  position: relative;
  z-index: 2;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 100px 1.5rem 3rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 80px 1rem 2rem;
  }
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 2.5rem;
`;

const Badge = styled.span`
  display: inline-block;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.accent1};
  margin-bottom: 1rem;
  opacity: 0.95;
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  background: ${({ theme }) => theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.75rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2.25rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.light};
  opacity: 0.85;
  font-style: italic;
`;

const Card = styled(motion.div)`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(12px);
  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding: 2rem;
  box-shadow: ${({ theme }) => theme.shadows.large};
  border: 1px solid rgba(255, 255, 255, 0.15);

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 1.5rem;
  }
`;

const SongInfo = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const SongTitle = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.light};
`;

const SpotifyEmbed = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;

  iframe {
    display: block;
  }
`;

const AudioWrap = styled.div`
  margin-top: 0.5rem;

  audio {
    border-radius: ${({ theme }) => theme.borderRadius.medium};
    outline: none;
  }
`;

const Placeholder = styled.div`
  text-align: center;
  padding: 2rem 1rem;
  color: ${({ theme }) => theme.colors.light};
  opacity: 0.9;
`;

const PlaceholderTitle = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: ${({ theme }) => theme.colors.accent1};
`;

const PlaceholderText = styled.p`
  font-size: 0.9rem;
  line-height: 1.6;

  code {
    background: rgba(255, 255, 255, 0.15);
    padding: 0.15em 0.4em;
    border-radius: 4px;
    font-size: 0.85em;
  }
`;

const Footer = styled(motion.p)`
  text-align: center;
  margin-top: 2.5rem;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.accent1};
  font-weight: 500;
`;

export default Valentines2026;
